<template>
    <main-layout class="bg-gray-100 min-h-screen flex flex-col">
        <!-- Bandeau dégradé + header -->
        <div class="bg-gradient-to-b from-secondary to-primary">
            <!-- On masque l'ancien header si nécessaire -->
            <app-header icon="factory" class="!hidden">Ajout détenteur</app-header>

            <header class="text-white pt-4 pb-12 px-4 flex items-center justify-between">
                <!-- Titre + retour -->
                <h1 class="text-2xl flex items-center gap-3">
                    <router-link
                        class="inline-flex items-center justify-center h-10 w-10 rounded-lg bg-white/15 hover:bg-white/25 transition"
                        :to="{ name: 'liste-detenteurs' }">
                        <i class="inline-block h-6 w-6 mask mask-arrow-left bg-white"></i>
                    </router-link>
                    <span>Ajout détenteur</span>
                </h1>

                <!-- (vide) : pas d’action côté droit pour cette page -->
                <span class="opacity-0 select-none">.</span>
            </header>
        </div>

        <!-- Corps arrondi + dégradé clair -->
        <main class="bg-gradient-to-b from-[#F8FBFF] to-[#E2E9F5] rounded-t-3xl -mt-6 flex-1 shadow-xl pb-28">
            <div class="m-2 shadow-2xl rounded-lg overflow-hidden mb-24">
                <!-- Carte formulaire -->
                <div class="bg-white p-4">
                    <form method="post" id="detenteur" class="space-y-6" @submit.prevent="sendForm">
                        <!-- Bloc: Informations générales -->
                        <div>
                            <h2 class="text-lg font-medium mb-4 uppercase text-secondary">Informations générales</h2>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <!-- Type -->
                                <div>
                                    <label class="label">Type</label>
                                    <select v-model.trim="type" name="type" id="type" class="input">
                                        <option selected>Société</option>
                                        <option>Particulier</option>
                                        <option>Association</option>
                                    </select>
                                </div>

                                <!-- Code client -->
                                <div>
                                    <label class="label">Code client</label>
                                    <input title="clientCode" name="code-client" id="code-client" type="text"
                                        v-model.trim="code_client" maxlength="255" class="input" />
                                </div>

                                <!-- Raison sociale / Nom -->
                                <div class="md:col-span-2">
                                    <label class="label">
                                        Raison sociale / Nom <span class="text-alert">*</span>
                                    </label>
                                    <input title="company" name="raison_sociale" id="raison_sociale" type="text"
                                        v-model.trim="raison_sociale" maxlength="255" class="input" />
                                </div>

                                <!-- SIRET (masqué pour Particulier) -->
                                <div v-show="this.type !== 'Particulier'">
                                    <label class="label">SIRET</label>
                                    <cleave :options="siretOpt" name="siret" id="siret" type="text" v-model.trim="siret"
                                        class="input" />
                                </div>
                            </div>

                            <!-- Adresse / CP / Ville -->
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                <!-- Suggestion géoloc -->
                                <div v-if="suggestion"
                                    class="md:col-span-2 flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700">
                                    <i class="inline-block h-4 w-4 shrink-0 mask mask-location bg-blue-500"></i>
                                    <span class="flex-1 truncate">{{ suggestion.preview }}</span>
                                    <button type="button" @click="applySuggestion"
                                        class="shrink-0 font-medium underline underline-offset-2">Utiliser</button>
                                    <button type="button" @click="suggestion = null"
                                        class="shrink-0 opacity-50 hover:opacity-100 text-base leading-none">✕</button>
                                </div>

                                <!-- Adresse -->
                                <div class="md:col-span-2">
                                    <label class="label">
                                        Adresse <span class="text-alert">*</span>
                                    </label>
                                    <input title="address" name="adresse" id="adresse" type="text"
                                        v-model.trim="adresse" maxlength="255" class="input" />
                                </div>

                                <!-- Complément d'adresse -->
                                <div class="md:col-span-2">
                                    <label class="label">Complément d'adresse</label>
                                    <input title="moreAddress" name="completemnt_adresse" id="completemnt_adresse"
                                        type="text" v-model.trim="complement_adresse" maxlength="255" class="input" />
                                </div>

                                <!-- Code postal -->
                                <div>
                                    <label class="label">
                                        Code postal <span class="text-alert">*</span>
                                    </label>
                                    <cleave :options="cpOpt" name="adresse_cp" id="adresse_cp" type="text"
                                        v-model.trim="code_postal" class="input" />
                                </div>

                                <!-- Ville -->
                                <div>
                                    <label class="label">
                                        Ville <span class="text-alert">*</span>
                                    </label>
                                    <input title="city" name="adresse_ville" id="adresse_ville" type="text"
                                        v-model.trim="ville" maxlength="255" class="input" />
                                </div>
                            </div>

                            <!-- Téléphone / Email -->
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                <!-- Téléphone -->
                                <div>
                                    <label class="label">Téléphone</label>
                                    <cleave :options="telOpt" name="telephone" id="telephone" type="text"
                                        v-model.trim="telephone" inputmode="tel" class="input" />
                                </div>

                                <!-- Email -->
                                <div>
                                    <label class="label">
                                        Email <span class="text-[11px] text-gray-500">(réception des fiches)</span>
                                    </label>
                                    <input title="email" name="email" id="email" type="text" v-model.trim="email"
                                        maxlength="255" class="input" />
                                </div>
                            </div>
                        </div>

                        <!-- Bloc: Contact -->
                        <div>
                            <h2 class="text-lg font-medium mb-4 uppercase text-secondary">Contact</h2>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <!-- Nom / Prénom -->
                                <div class="md:col-span-2">
                                    <label class="label">Nom / Prénom</label>
                                    <input title="fullName" name="nom_contact" id="nom_contact" type="text"
                                        v-model.trim="nom" maxlength="255" class="input" />
                                </div>

                                <!-- Portable -->
                                <div>
                                    <label class="label">Téléphone portable</label>
                                    <cleave :options="telOpt" name="tel_contact" id="tel_contact" type="text"
                                        v-model.trim="portable" inputmode="tel" class="input" />
                                </div>

                                <!-- Ligne directe -->
                                <div>
                                    <label class="label">Ligne directe</label>
                                    <cleave :options="telOpt" name="ligne_directe_contact" id="ligne_directe_contact"
                                        type="text" v-model.trim="direct" inputmode="tel" class="input" />
                                </div>
                            </div>
                        </div>

                        <!-- Submit -->
                        <div class="pt-2">
                            <button type="submit" name="new"
                                class="w-full sm:w-auto bg-primary text-white font-semibold tracking-wide px-5 py-3 rounded-xl shadow hover:shadow-md active:scale-[0.99] transition">
                                AJOUTER
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </main>
    </main-layout>
</template>

<style scoped>
.text-alert {
    color: #c31616;
}
</style>



<script>
import Cleave from 'vue-cleave';
import Detenteur from '../../js/models/Detenteur';

require('cleave.js/dist/addons/cleave-phone.fr');

export default {

    data() {
        return {
            type: 'Société',
            raison_sociale: '',
            code_client: '',
            siret: '',
            ville: '',
            code_postal: '',
            adresse: '',
            complement_adresse: '',
            telephone: '',
            email: '',
            nom: '',
            portable: '',
            direct: '',
            actif: true,
            lat: null,
            lng: null,
            suggestion: null,
            installations: [],
            siretOpt: {
                blocks: [3, 3, 3, 5],
                delimiter: ' ',
                numericOnly: true
            },
            cpOpt: {
                blocks: [2, 3, 20],
                delimiter: ' ',
                numericOnly: false
            },
            telOpt: {
                phone: true,
                phoneRegionCode: 'fr'
            }

        }
    },

    components: {
        Cleave
    },

    async mounted() {
        if (window.isMobile()) {
            if (typeof window.flutter_inappwebview !== 'undefined' && window.flutter_inappwebview !== null) {
                try {
                    let result = await window.flutter_inappwebview.callHandler('requestGeolocation');
                    if (result !== false) {
                        this.lat = result.latitude;
                        this.lng = result.longitude;
                        await this.suggestAddressFromLocation(result.latitude, result.longitude);
                    }
                } catch (e) {
                    // Geolocation is optional — silent fail
                }
            }
        }
    },

    methods: {

        async suggestAddressFromLocation(lat, lng) {
            try {
                const response = await fetch(
                    `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`,
                    { headers: { 'Accept-Language': 'fr' } }
                );
                const data = await response.json();
                const addr = data.address;
                if (!addr) return;

                const adresse = [addr.house_number, addr.road].filter(Boolean).join(' ');
                const cp = (addr.postcode || '').replace(/\s/g, '');
                const ville = addr.city || addr.town || addr.village || addr.municipality || '';
                const preview = [adresse, [cp, ville].filter(Boolean).join(' ')].filter(Boolean).join(', ');

                if (preview) this.suggestion = { adresse, cp, ville, preview };
            } catch (e) {
                // Reverse geocoding is optional — silent fail
            }
        },

        async applySuggestion() {
            const { adresse, cp, ville } = this.suggestion;
            this.suggestion = null;

            if (adresse) this.adresse = adresse;
            if (ville) this.ville = ville;
            if (cp) {
                this.code_postal = cp;
                await this.$nextTick();
                const cpEl = document.getElementById('adresse_cp');

                if (cpEl) {
                    cpEl.value = cp;
                    cpEl.dispatchEvent(new Event('input', { bubbles: true }));
                }
            }
        },

        sendForm() {

            let router = this.$router;
            let newDet = this.$data;

            let errors = [];
            let txt = '';

            if (newDet.raison_sociale === "") {
                errors.push('la raison sociale / Nom')
            }
            if (newDet.adresse === "") {
                errors.push("l'adresse")
            }
            if (newDet.ville === "") {
                errors.push('la ville')
            }
            if (newDet.code_postal === "") {
                errors.push('le code postal')
            }

            txt = errors.join(', ');

            if ((newDet.siret.length !== 17 && newDet.siret.length !== 14) && newDet.siret.length !== 0) {
                txt += '<br/>SIRET invalide (14 chiffres)'
            }

            if (txt !== '') {

                swal('Information(s) manquante(s) :', 'Veuillez renseigner ' + txt + '.', 'error')

            } else {
                delete newDet.cpOpt;
                delete newDet.siretOpt;
                delete newDet.telOpt;


                Detenteur.addDetenteurOffline(newDet).then(() => {
                    /*
                    if (navigator.onLine) {
                        Synchro.syncDetenteurs(true).then(() => {
                            router.push({name:'liste-detenteurs'});
                            swal('Détenteur ajouté', 'Le détenteur a bien été ajouté', 'success');
                        })
                    }else{
                    */
                    router.push({ name: 'liste-detenteurs' });
                    swal('Détenteur ajouté', 'Le détenteur a bien été ajouté', 'success')
                    /*}*/
                })
            }
        }
    },
}

</script>



// WEBPACK FOOTER //
// NewDetenteur.vue?9283d1ae